import React, { useEffect, useRef } from 'react';
import { api } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useLocalizedNavigate } from '../i18n/useLocalizedNav';

const GSI_SRC = 'https://accounts.google.com/gsi/client';

// Load the GSI SDK on demand. Returns a promise that resolves when
// window.google is available. Reuses an existing tag if a previous
// mount already injected one (and is still loading or loaded), so the
// SDK fetches exactly once per page load no matter how many
// GoogleLogin components mount.
function loadGsi(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();
  if (window.google?.accounts?.id) return Promise.resolve();

  const existing = document.querySelector<HTMLScriptElement>(
    'script[data-gsi="1"]',
  );
  if (existing) {
    if (window.google?.accounts?.id) return Promise.resolve();
    return new Promise((resolve, reject) => {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', reject, { once: true });
    });
  }

  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = GSI_SRC;
    s.async = true;
    s.defer = true;
    s.dataset.gsi = '1';
    s.onload = () => resolve();
    s.onerror = reject;
    document.head.appendChild(s);
  });
}

const GoogleLogin: React.FC = () => {
  const { login } = useAuth();
  const navigate = useLocalizedNavigate();
  const googleBtnRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
    if (!clientId) {
      console.error('VITE_GOOGLE_CLIENT_ID is not set');
      return;
    }

    let cancelled = false;
    const handleCredentialResponse = async (response: { credential: string }) => {
      try {
        const data = await api.post('/auth/google', { token: response.credential });
        if (data.user) {
          login(data.user);
          navigate('/');
        } else {
          console.error('Google login failed:', data.error);
        }
      } catch (err) {
        console.error('Google login error:', err);
      }
    };

    loadGsi()
      .then(() => {
        if (cancelled || !googleBtnRef.current) return;
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: handleCredentialResponse,
        });
        window.google.accounts.id.renderButton(googleBtnRef.current, {
          theme: 'outline',
          size: 'large',
          width: '100%',
          text: 'continue_with',
          shape: 'rectangular',
        });
      })
      .catch((err) => console.error('Failed to load Google Identity Services:', err));

    return () => {
      cancelled = true;
    };
  }, [login, navigate]);

  return <div ref={googleBtnRef} className="w-full" />;
};

export default GoogleLogin;

// Add type definition for window.google
declare global {
  interface Window {
    google: any;
  }
}
